import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { useFinancialYear } from '../context/FinancialYearContext';
import { useFunderGate } from '../components/FunderGate';
import { toast } from 'sonner';
import {
  RefreshCw, IndianRupee, TrendingUp, TrendingDown, Wallet, Plus, Trash2,
  Landmark, Receipt, ArrowDownCircle, ArrowUpCircle, Calculator, Calendar, LockKeyhole
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);
const fmt2 = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(v || 0);

const FunderPayoutPage = () => {
  const { getAuthHeaders } = useAuth();
  const { selectedFY } = useFinancialYear();
  const { gateToken, clear: clearGate } = useFunderGate();
  const headers = () => ({ ...getAuthHeaders(), 'X-Funder-Gate': gateToken });

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState('');
  const [tab, setTab] = useState('revenue');

  // Project drilldown
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillType, setDrillType] = useState('corporate');
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  // Fixed costs
  const [fcDialog, setFcDialog] = useState(false);
  const [fcEdit, setFcEdit] = useState(null);
  const [fcForm, setFcForm] = useState({ head: '', monthly_amount: '', notes: '' });

  // Ad-hoc
  const [adhocDialog, setAdhocDialog] = useState(false);
  const [adhocForm, setAdhocForm] = useState({ date: new Date().toISOString().slice(0, 10), head: '', amount: '', description: '' });

  // Quarterly
  const [quarterly, setQuarterly] = useState(null);
  const [quarterlyLoading, setQuarterlyLoading] = useState(false);

  // Generic drill dialog
  const [drillCardOpen, setDrillCardOpen] = useState(false);
  const [drillCardTitle, setDrillCardTitle] = useState('');
  const [drillCardData, setDrillCardData] = useState(null);
  const [drillCardLoading, setDrillCardLoading] = useState(false);
  const [drillCardKind, setDrillCardKind] = useState('');

  // Quarter calculation breakdown dialog
  const [calcOpen, setCalcOpen] = useState(false);
  const [calcQ, setCalcQ] = useState(null);

  const fetchSummary = useCallback(async () => {
    if (!selectedFY) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/funder-payout/summary?financial_year=${selectedFY}`, { headers: headers() });
      setSummary(r.data);
    } catch (err) {
      toast.error('Failed to load summary');
    } finally {
      setLoading(false);
    }
  }, [selectedFY, gateToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncProgress('Starting...');
    try {
      const r = await axios.post(`${API}/crm/sync-corp-tracker`, {}, { headers: headers() });
      const jobId = r.data?.job_id;
      toast.info('CRM sync started...');
      const start = Date.now();
      while (Date.now() - start < 5 * 60 * 1000) {
        await new Promise(res => setTimeout(res, 3000));
        setSyncProgress(`Syncing... (${Math.round((Date.now() - start) / 1000)}s)`);
        const poll = await axios.get(`${API}/zoho/sync-jobs/${jobId}`, { headers: headers() });
        if (poll.data?.status === 'success') {
          toast.success(`Synced ${poll.data.result?.upserted || 0} CRM records`);
          fetchSummary();
          return;
        }
        if (poll.data?.status === 'failed') {
          toast.error(poll.data?.error || 'Sync failed');
          return;
        }
      }
      toast.error('Sync timed out');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Sync failed');
    } finally {
      setSyncing(false);
      setSyncProgress('');
    }
  };

  const openDrill = async (type) => {
    setDrillType(type);
    setDrillOpen(true);
    setDrillLoading(true);
    setDrillData(null);
    try {
      const r = await axios.get(`${API}/funder-payout/projects?financial_year=${selectedFY}&type=${type}`, { headers: headers() });
      setDrillData(r.data);
    } catch (err) {
      toast.error('Failed to load drilldown');
    } finally {
      setDrillLoading(false);
    }
  };

  const fetchQuarterly = useCallback(async () => {
    if (!selectedFY) return;
    setQuarterlyLoading(true);
    try {
      const r = await axios.get(`${API}/funder-payout/quarterly?financial_year=${selectedFY}`, { headers: headers() });
      setQuarterly(r.data);
    } catch (err) {
      toast.error('Failed to load quarterly');
    } finally {
      setQuarterlyLoading(false);
    }
  }, [selectedFY, gateToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (tab === 'quarterly') fetchQuarterly(); }, [tab, fetchQuarterly]);

  const openCardDrill = async (kind, title) => {
    setDrillCardKind(kind);
    setDrillCardTitle(title);
    setDrillCardOpen(true);
    setDrillCardLoading(true);
    setDrillCardData(null);
    try {
      let url = '';
      if (kind === 'invoices_billed') url = `${API}/funder-payout/invoices?financial_year=${selectedFY}`;
      else if (kind === 'invoices_paid') url = `${API}/funder-payout/invoices?financial_year=${selectedFY}&status=paid`;
      else if (kind === 'invoices_pending') url = `${API}/funder-payout/invoices?financial_year=${selectedFY}&status=unpaid`;
      else if (kind === 'tds') url = `${API}/funder-payout/tds-drill?financial_year=${selectedFY}`;
      else if (kind === 'loans') url = `${API}/funder-payout/loans`;
      else if (kind === 'corp_costs') url = `${API}/funder-payout/projects?financial_year=${selectedFY}&type=corporate`;
      else if (kind === 'venue_costs') url = `${API}/funder-payout/projects?financial_year=${selectedFY}&type=venue`;
      else return;
      const r = await axios.get(url, { headers: headers() });
      setDrillCardData(r.data);
    } catch (err) {
      toast.error('Failed to load drilldown');
    } finally {
      setDrillCardLoading(false);
    }
  };

  const saveFixedCost = async () => {
    const payload = {
      head: fcForm.head.trim(),
      monthly_amount: parseFloat(fcForm.monthly_amount),
      notes: fcForm.notes
    };
    if (!payload.head || !payload.monthly_amount || payload.monthly_amount <= 0) {
      toast.error('Head and monthly amount are required');
      return;
    }
    try {
      if (fcEdit) {
        await axios.put(`${API}/funder-payout/fixed-costs/${fcEdit.id}`, payload, { headers: headers() });
        toast.success('Updated');
      } else {
        await axios.post(`${API}/funder-payout/fixed-costs`, payload, { headers: headers() });
        toast.success('Added');
      }
      setFcDialog(false); setFcEdit(null);
      setFcForm({ head: '', monthly_amount: '', notes: '' });
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const deleteFixedCost = async (id) => {
    if (!window.confirm('Delete this fixed cost head?')) return;
    try {
      await axios.delete(`${API}/funder-payout/fixed-costs/${id}`, { headers: headers() });
      toast.success('Deleted');
      fetchSummary();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const saveAdhoc = async () => {
    const payload = {
      date: adhocForm.date,
      head: adhocForm.head.trim(),
      amount: parseFloat(adhocForm.amount),
      description: adhocForm.description,
      financial_year: selectedFY
    };
    if (!payload.date || !payload.head || !payload.amount || payload.amount <= 0) {
      toast.error('Date, head, and amount are required');
      return;
    }
    try {
      await axios.post(`${API}/funder-payout/adhoc`, payload, { headers: headers() });
      toast.success('Added');
      setAdhocDialog(false);
      setAdhocForm({ date: new Date().toISOString().slice(0, 10), head: '', amount: '', description: '' });
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const deleteAdhoc = async (id) => {
    if (!window.confirm('Delete this ad-hoc cost?')) return;
    try {
      await axios.delete(`${API}/funder-payout/adhoc/${id}`, { headers: headers() });
      toast.success('Deleted');
      fetchSummary();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  if (!selectedFY) {
    return <div className="p-8 text-center text-slate-500">Please select a financial year.</div>;
  }
  if (loading && !summary) {
    return <div className="p-8 text-center text-slate-500">Loading...</div>;
  }
  if (!summary) return null;

  const cp = summary.cash_position;
  const rec = summary.funder_reconciliation;

  return (
    <div className="space-y-6" data-testid="funder-payout-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-indigo-950">Funder Payout</h1>
          <p className="text-sm text-slate-500">Revenue, funder fees, fixed costs & live cash position · FY {selectedFY}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleSync} disabled={syncing} className="bg-indigo-950 hover:bg-indigo-900" data-testid="sync-crm-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? (syncProgress || 'Syncing...') : 'Sync from CRM'}
          </Button>
          <Button variant="outline" onClick={clearGate} data-testid="funder-lock-btn" title="Lock Funder section">
            <LockKeyhole className="w-4 h-4 mr-2" /> Lock
          </Button>
        </div>
      </div>

      {/* Top-line KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrill('corporate')} data-testid="kpi-revenue">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center"><TrendingUp className="w-5 h-5 text-emerald-600" /></div>
              <div>
                <p className="text-xs text-emerald-600">Revenue (excl GST)</p>
                <p className="text-xl font-bold text-emerald-700">{fmt(summary.totals.revenue_excl_gst)}</p>
                <p className="text-xs text-slate-500">{summary.corporate.count + summary.venue.count} projects</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white cursor-pointer hover:shadow-md transition-shadow" onClick={() => setTab('quarterly')} data-testid="kpi-funder-fee">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center"><Calculator className="w-5 h-5 text-amber-600" /></div>
              <div>
                <p className="text-xs text-amber-600">Funder Take</p>
                <p className="text-xl font-bold text-amber-700">{fmt(summary.totals.funder_fee_total)}</p>
                <p className="text-xs text-slate-500">10% corp + 70% venue NP</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-rose-200 bg-gradient-to-br from-rose-50 to-white" data-testid="kpi-funder-owes">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center"><ArrowDownCircle className="w-5 h-5 text-rose-600" /></div>
              <div>
                <p className="text-xs text-rose-600">Funder Owes Us</p>
                <p className="text-xl font-bold text-rose-700">{fmt(rec.funder_owes_us)}</p>
                <p className="text-xs text-slate-500">Fixed + ad-hoc paid</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-white" data-testid="kpi-our-share">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center"><Wallet className="w-5 h-5 text-indigo-600" /></div>
              <div>
                <p className="text-xs text-indigo-600">Corporate Team Take</p>
                <p className="text-xl font-bold text-indigo-700">{fmt(summary.totals.our_share_total)}</p>
                <p className="text-xs text-slate-500">After funder fee</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="funder-payout-tabs">
          <TabsTrigger value="revenue" data-testid="tab-revenue"><TrendingUp className="w-4 h-4 mr-1" /> Revenue & Fees</TabsTrigger>
          <TabsTrigger value="quarterly" data-testid="tab-quarterly"><Calendar className="w-4 h-4 mr-1" /> Quarterly</TabsTrigger>
          <TabsTrigger value="fixed" data-testid="tab-fixed"><Receipt className="w-4 h-4 mr-1" /> Monthly Fixed Costs</TabsTrigger>
          <TabsTrigger value="adhoc" data-testid="tab-adhoc"><Calendar className="w-4 h-4 mr-1" /> Ad-hoc</TabsTrigger>
          <TabsTrigger value="cash" data-testid="tab-cash"><Wallet className="w-4 h-4 mr-1" /> Cash Position</TabsTrigger>
        </TabsList>

        {/* ============ REVENUE & FEES ============ */}
        <TabsContent value="revenue" className="mt-6 space-y-4" data-testid="pane-revenue">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border-2 border-indigo-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrill('corporate')} data-testid="corp-card">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-indigo-950">Corporate Training</h3>
                    <p className="text-xs text-slate-500">{summary.corporate.count} projects · fee 10% × revenue (excl GST)</p>
                  </div>
                  <Landmark className="w-6 h-6 text-indigo-600" />
                </div>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <Row label="Revenue (excl GST)" value={fmt(summary.corporate.revenue_excl_gst)} />
                  <Row label="Total cost" value={fmt(summary.corporate.total_cost)} />
                  <Row label="Net profit (W/O GST)" value={fmt(summary.corporate.net_profit_wo_gst)} highlight="emerald" />
                </div>
                <div className="grid grid-cols-2 gap-3 pt-3 border-t">
                  <div className="rounded-lg border-2 border-amber-200 bg-amber-50 p-3" data-testid="corp-funder-take">
                    <p className="text-xs text-amber-700 font-semibold uppercase tracking-wide">Funder take (10%)</p>
                    <p className="text-2xl font-bold text-amber-700 mt-1 font-mono">{fmt(summary.corporate.funder_fee)}</p>
                  </div>
                  <div className="rounded-lg border-2 border-indigo-300 bg-indigo-50 p-3" data-testid="corp-team-take">
                    <p className="text-xs text-indigo-700 font-semibold uppercase tracking-wide">Corporate Team take</p>
                    <p className="text-2xl font-bold text-indigo-700 mt-1 font-mono">{fmt(summary.corporate.our_share)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-purple-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrill('venue')} data-testid="venue-card">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-purple-950">Venue</h3>
                    <p className="text-xs text-slate-500">{summary.venue.count} projects · fee 70% × Net Profit (W/O GST)</p>
                  </div>
                  <Landmark className="w-6 h-6 text-purple-600" />
                </div>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <Row label="Revenue (excl GST)" value={fmt(summary.venue.revenue_excl_gst)} />
                  <Row label="Total cost" value={fmt(summary.venue.total_cost)} />
                  <Row label="Net profit (W/O GST)" value={fmt(summary.venue.net_profit_wo_gst)} highlight="emerald" />
                </div>
                <div className="grid grid-cols-2 gap-3 pt-3 border-t">
                  <div className="rounded-lg border-2 border-amber-200 bg-amber-50 p-3" data-testid="venue-funder-take">
                    <p className="text-xs text-amber-700 font-semibold uppercase tracking-wide">Funder take (70% of NP)</p>
                    <p className="text-2xl font-bold text-amber-700 mt-1 font-mono">{fmt(summary.venue.funder_fee)}</p>
                  </div>
                  <div className="rounded-lg border-2 border-purple-300 bg-purple-50 p-3" data-testid="venue-team-take">
                    <p className="text-xs text-purple-700 font-semibold uppercase tracking-wide">Corporate Team take (30%)</p>
                    <p className="text-2xl font-bold text-purple-700 mt-1 font-mono">{fmt(summary.venue.our_share)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          <p className="text-xs text-slate-500">Click a card for per-project drilldown. Data refreshes after &quot;Sync from CRM&quot;.</p>
        </TabsContent>

        {/* ============ QUARTERLY ============ */}
        <TabsContent value="quarterly" className="mt-6 space-y-4" data-testid="pane-quarterly">
          {quarterlyLoading && <p className="text-slate-500">Loading...</p>}
          {!quarterlyLoading && quarterly && (
            <Card>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm" data-testid="quarterly-table">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left py-3 px-4 font-medium text-slate-500">Metric</th>
                      {['Q1', 'Q2', 'Q3', 'Q4'].map(q => (
                        <th key={q} className="text-right py-3 px-4 font-medium text-slate-500">
                          {q}<br /><span className="text-xs font-normal text-slate-400">{quarterly.quarters[q].period.start}—{quarterly.quarters[q].period.end}</span>
                        </th>
                      ))}
                      <th className="text-right py-3 px-4 font-medium text-slate-500">FY Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { label: 'Corp Revenue (excl GST)', f: q => q.corporate.revenue_excl_gst },
                      { label: 'Corp Funder Take (10%)', f: q => q.corporate.funder_fee, color: 'amber' },
                      { label: 'Corp Team Take', f: q => q.corporate.our_share, color: 'emerald', bold: true },
                      { label: 'Venue Revenue (excl GST)', f: q => q.venue.revenue_excl_gst },
                      { label: 'Venue Net Profit (W/O GST)', f: q => q.venue.net_profit_wo_gst, color: 'emerald' },
                      { label: 'Venue Funder Take (70%)', f: q => q.venue.funder_fee, color: 'amber' },
                      { label: 'Venue Team Take (30%)', f: q => q.venue.our_share, color: 'emerald', bold: true },
                      { label: '— Total Corporate Team Take —', f: q => q.our_share_total, color: 'emerald', bold: true, sep: true },
                      { label: 'Total Funder Take', f: q => q.funder_fee_total, color: 'amber', bold: true },
                      { label: 'TDS already paid to funder', f: q => q.tds_withheld, color: 'rose' },
                      { label: '— Net We Owe Funder (after TDS) —', f: q => q.net_we_owe_funder_after_tds, color: 'rose', bold: true, sep: true },
                      { label: 'Invoices Billed', f: q => q.invoices_billed },
                      { label: 'Payments Received', f: q => q.payments_received, color: 'emerald' },
                      { label: 'Fixed Costs (3 × monthly)', f: q => q.fixed_costs, color: 'rose' },
                      { label: 'Ad-hoc Costs', f: q => q.adhoc_costs, color: 'rose' }
                    ].map((row, i) => {
                      const c = { amber: 'text-amber-700', emerald: 'text-emerald-700', rose: 'text-rose-700' }[row.color] || 'text-slate-700';
                      const cls = `py-3 px-4 text-right font-mono ${c} ${row.bold ? 'font-bold' : ''}`;
                      const fyTotal = ['Q1', 'Q2', 'Q3', 'Q4'].reduce((acc, q) => acc + row.f(quarterly.quarters[q]), 0);
                      return (
                        <tr key={i} className={`border-b ${row.sep ? 'bg-slate-50/60' : ''}`} data-testid={`q-row-${i}`}>
                          <td className="py-3 px-4 font-medium text-slate-700">{row.label}</td>
                          {['Q1', 'Q2', 'Q3', 'Q4'].map(q => (
                            <td key={q} className={cls}>{fmt(row.f(quarterly.quarters[q]))}</td>
                          ))}
                          <td className={`${cls} bg-slate-50`}>{fmt(fyTotal)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {/* Per-quarter Funder Share / Corporate Team Share cards */}
          {!quarterlyLoading && quarterly && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-700">Per-Quarter Share Breakdown</h3>
                <p className="text-xs text-slate-500">Click any quarter card to see the math</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {['Q1', 'Q2', 'Q3', 'Q4'].map(qk => {
                  const v = quarterly.quarters[qk];
                  const funderShare = v.funder_fee_total;
                  const teamShare = v.our_share_total;
                  const reimb = (v.fixed_costs || 0) + (v.adhoc_costs || 0);
                  const finalOwe = (v.net_we_owe_funder_after_tds || 0) - reimb;
                  return (
                    <Card
                      key={qk}
                      className="border-2 border-slate-200 hover:border-indigo-300 cursor-pointer hover:shadow-md transition-all"
                      onClick={() => { setCalcQ({ key: qk, ...v }); setCalcOpen(true); }}
                      data-testid={`q-share-card-${qk}`}
                    >
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="font-bold text-indigo-950">{qk}</p>
                          <p className="text-xs text-slate-400">{v.period.start}—{v.period.end}</p>
                        </div>
                        <div className="rounded-md border-2 border-amber-200 bg-amber-50 p-2">
                          <p className="text-[10px] text-amber-700 font-semibold uppercase tracking-wide">Funder Share</p>
                          <p className="text-lg font-bold text-amber-700 font-mono">{fmt(funderShare)}</p>
                        </div>
                        <div className="rounded-md border-2 border-indigo-300 bg-indigo-50 p-2">
                          <p className="text-[10px] text-indigo-700 font-semibold uppercase tracking-wide">Corporate Team Share</p>
                          <p className="text-lg font-bold text-indigo-700 font-mono">{fmt(teamShare)}</p>
                        </div>
                        {v.tds_withheld > 0 && (
                          <p className="text-[10px] text-purple-700 bg-purple-50 px-2 py-1 rounded">TDS already paid to funder: <strong>{fmt(v.tds_withheld)}</strong></p>
                        )}
                        <div className="border-t border-dashed border-slate-200 pt-2 space-y-1">
                          <p className="text-[10px] text-slate-500 flex justify-between"><span>Fixed + Ad-hoc</span><span className="font-mono font-semibold text-emerald-700" data-testid={`q-reimb-${qk}`}>{fmt(reimb)}</span></p>
                          <p className="text-[10px] text-slate-500 flex justify-between">
                            <span className={`font-semibold ${finalOwe >= 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{finalOwe >= 0 ? 'Final · We owe Funder' : 'Final · Funder owes Us'}</span>
                            <span className={`font-mono font-bold ${finalOwe >= 0 ? 'text-rose-700' : 'text-emerald-700'}`} data-testid={`q-final-${qk}`}>{fmt(Math.abs(finalOwe))}</span>
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </TabsContent>

        {/* ============ MONTHLY FIXED COSTS ============ */}
        <TabsContent value="fixed" className="mt-6 space-y-4" data-testid="pane-fixed">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-700">Monthly Fixed Costs · Annual Total: <span className="text-rose-700">{fmt(summary.fixed_costs.annual_total)}</span></h3>
              <p className="text-xs text-slate-500">Monthly × 12 for the FY · paid from loan account, funder reimburses</p>
            </div>
            <Button onClick={() => { setFcEdit(null); setFcForm({ head: '', monthly_amount: '', notes: '' }); setFcDialog(true); }} className="bg-indigo-950 hover:bg-indigo-900" data-testid="add-fixed-cost">
              <Plus className="w-4 h-4 mr-1" /> Add Head
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm" data-testid="fixed-costs-table">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Head</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-500">Monthly</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-500">Annual (12 × monthly)</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Notes</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.fixed_costs.items.map(f => (
                    <tr key={f.id} className="border-b" data-testid={`fc-row-${f.id}`}>
                      <td className="py-3 px-4 font-medium">{f.head}</td>
                      <td className="py-3 px-4 text-right font-mono">{fmt(f.monthly_amount)}</td>
                      <td className="py-3 px-4 text-right font-mono font-semibold text-rose-700">{fmt(f.annual_amount)}</td>
                      <td className="py-3 px-4 text-slate-500 text-xs">{f.notes || '-'}</td>
                      <td className="py-3 px-4 text-center">
                        <Button variant="ghost" size="sm" onClick={() => { setFcEdit(f); setFcForm({ head: f.head, monthly_amount: String(f.monthly_amount), notes: f.notes || '' }); setFcDialog(true); }} data-testid={`fc-edit-${f.id}`}>Edit</Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteFixedCost(f.id)} className="text-red-600" data-testid={`fc-delete-${f.id}`}><Trash2 className="w-3 h-3" /></Button>
                      </td>
                    </tr>
                  ))}
                  {summary.fixed_costs.items.length === 0 && (
                    <tr><td colSpan="5" className="py-6 text-center text-slate-400">No fixed cost heads yet. Click &quot;Add Head&quot; to start.</td></tr>
                  )}
                </tbody>
                {summary.fixed_costs.items.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                      <td className="py-3 px-4">TOTAL</td>
                      <td className="py-3 px-4 text-right font-mono">{fmt(summary.fixed_costs.monthly_total)}</td>
                      <td className="py-3 px-4 text-right font-mono text-rose-700">{fmt(summary.fixed_costs.annual_total)}</td>
                      <td colSpan="2"></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ AD-HOC ============ */}
        <TabsContent value="adhoc" className="mt-6 space-y-4" data-testid="pane-adhoc">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-700">Ad-hoc Funder Costs · Total: <span className="text-rose-700">{fmt(summary.adhoc_costs.total)}</span></h3>
              <p className="text-xs text-slate-500">One-off payments made for funder · funder reimburses</p>
            </div>
            <Button onClick={() => setAdhocDialog(true)} className="bg-indigo-950 hover:bg-indigo-900" data-testid="add-adhoc">
              <Plus className="w-4 h-4 mr-1" /> Add Ad-hoc
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm" data-testid="adhoc-table">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Date</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Head</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Description</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-500">Amount</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-500">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.adhoc_costs.items.map(a => (
                    <tr key={a.id} className="border-b" data-testid={`adhoc-row-${a.id}`}>
                      <td className="py-3 px-4 text-slate-600">{a.date}</td>
                      <td className="py-3 px-4 font-medium">{a.head}</td>
                      <td className="py-3 px-4 text-slate-500 text-xs max-w-md truncate">{a.description || '-'}</td>
                      <td className="py-3 px-4 text-right font-mono font-semibold">{fmt(a.amount)}</td>
                      <td className="py-3 px-4 text-center">
                        <Button variant="ghost" size="sm" onClick={() => deleteAdhoc(a.id)} className="text-red-600" data-testid={`adhoc-delete-${a.id}`}><Trash2 className="w-3 h-3" /></Button>
                      </td>
                    </tr>
                  ))}
                  {summary.adhoc_costs.items.length === 0 && (
                    <tr><td colSpan="5" className="py-6 text-center text-slate-400">No ad-hoc entries for FY {selectedFY}.</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ CASH POSITION ============ */}
        <TabsContent value="cash" className="mt-6 space-y-4" data-testid="pane-cash">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <CashCard label="Loan Received" value={cp.loan_received} sub={`Online ${fmt(cp.loan_online)} + Deposit ${fmt(cp.loan_deposit)}`} color="emerald" Icon={ArrowDownCircle} onClick={() => openCardDrill('loans', 'Funder Loans')} testid="cash-loan" />
            <CashCard label="Revenue Invoiced" value={cp.revenue_invoiced} sub={`${cp.invoice_count} invoices`} color="blue" Icon={Receipt} onClick={() => openCardDrill('invoices_billed', 'Invoices Billed in FY')} testid="cash-invoiced" />
            <CashCard label="Revenue Received" value={cp.revenue_received} color="emerald" Icon={ArrowDownCircle} onClick={() => openCardDrill('invoices_paid', 'Paid / Partially Paid Invoices')} testid="cash-received" />
            <CashCard label="Revenue Pending" value={cp.revenue_pending} color="amber" Icon={Calendar} onClick={() => openCardDrill('invoices_pending', 'Unpaid Invoices')} testid="cash-pending" />
            <CashCard label="TDS Already Paid to Funder" value={cp.tds_withheld} sub="held as TDS on customer payments" color="purple" Icon={Calculator} onClick={() => openCardDrill('tds', 'TDS Withheld Breakdown')} testid="cash-tds" />
            <CashCard label="Project Costs (Corp)" value={summary.corporate.total_cost} color="rose" Icon={ArrowUpCircle} onClick={() => openCardDrill('corp_costs', 'Corporate Project Costs')} testid="cash-corp-costs" />
            <CashCard label="Project Costs (Venue)" value={summary.venue.total_cost} color="rose" Icon={ArrowUpCircle} onClick={() => openCardDrill('venue_costs', 'Venue Project Costs')} testid="cash-venue-costs" />
            <CashCard label="Fixed Costs (FY)" value={cp.fixed_costs_paid} color="rose" Icon={ArrowUpCircle} onClick={() => setTab('fixed')} testid="cash-fixed" />
          </div>

          <Card>
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-700">Funder Reconciliation (FY {selectedFY})</h3>
                <Badge className={rec.net_funder_balance >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}>
                  {rec.net_funder_balance >= 0 ? 'Funder owes us net' : 'We owe funder net'}
                </Badge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <CashCard label="Funder owes us" value={rec.funder_owes_us} sub="reimbursable fixed + ad-hoc" color="emerald" Icon={ArrowDownCircle} />
                <CashCard label="We owe funder (gross fees)" value={rec.we_owe_funder} sub="10% corp + 70% venue NP" color="rose" Icon={ArrowUpCircle} />
                <CashCard label="TDS already paid to funder" value={rec.tds_already_paid_to_funder} sub="customer withheld" color="purple" Icon={Calculator} onClick={() => openCardDrill('tds', 'TDS Withheld Breakdown')} testid="recon-tds" />
                <CashCard label="Net We Owe Funder (after TDS)" value={rec.net_we_owe_funder_after_tds} sub="gross fees − TDS" color="rose" Icon={ArrowUpCircle} testid="recon-net-owe" />
                <CashCard label="Net Funder Balance" value={Math.abs(rec.net_funder_balance)} sub={rec.net_funder_balance >= 0 ? 'in our favour' : 'in funder favour'} color={rec.net_funder_balance >= 0 ? 'emerald' : 'rose'} Icon={Calculator} testid="recon-net" />
              </div>
              <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded space-y-1">
                <p className="font-semibold text-slate-700">How this is computed:</p>
                <p>• <strong>Funder owes us</strong> = fixed_costs_annual + ad-hoc costs (paid by us, refundable)</p>
                <p>• <strong>We owe funder</strong> = 10% × corp revenue (excl GST) + 70% × venue net-profit (W/O GST)</p>
                <p>• <strong>TDS</strong> = sum of payments[].tds_withheld on invoices whose <code>crm_project_id</code> matches a CRM Corp_Tracker project with PO_Date in this FY. Payment date is not filtered — TDS withheld any time against this FY&apos;s projects is counted here.</p>
                <p>• <strong>Net we owe funder</strong> = we_owe_funder − TDS (cannot go below 0)</p>
                <p>• <strong>Net funder balance</strong> = funder_owes_us − net_we_owe_funder</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ===== Drilldown Dialog ===== */}
      <Dialog open={drillOpen} onOpenChange={setDrillOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="project-drill-dialog">
          <DialogHeader>
            <DialogTitle>{drillType === 'corporate' ? 'Corporate Training' : 'Venue'} · FY {selectedFY}</DialogTitle>
            <DialogDescription>
              {drillData && <>{drillData.count} projects · Total revenue (excl GST) {fmt(drillData.revenue_excl_gst)} · Funder fee {fmt(drillData.funder_fee)} ({drillData.funder_fee_basis})</>}
            </DialogDescription>
          </DialogHeader>
          {drillLoading && <div className="py-10 text-center text-slate-500">Loading...</div>}
          {!drillLoading && drillData && (
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-2 px-2">Proj ID</th>
                    <th className="text-left py-2 px-2">PO Date</th>
                    <th className="text-left py-2 px-2">Company</th>
                    <th className="text-left py-2 px-2">Training Name</th>
                    <th className="text-left py-2 px-2">Mode</th>
                    <th className="text-right py-2 px-2">Rev (excl GST)</th>
                    <th className="text-right py-2 px-2">Cost</th>
                    <th className="text-right py-2 px-2">NP (W/O GST)</th>
                    <th className="text-right py-2 px-2">Funder Take</th>
                    <th className="text-right py-2 px-2">Corp Team Take</th>
                    <th className="text-left py-2 px-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {drillData.projects.map(p => {
                    const rev = parseFloat(p.Amount_without_GST || 0);
                    const cost = parseFloat(p.Total_Costs || 0);
                    const npWoGst = parseFloat(p.Net_Profit_W_O_GST || 0);
                    const fee = drillType === 'corporate' ? rev * 0.10 : npWoGst * 0.70;
                    const ours = (rev - cost) - fee;
                    return (
                      <tr key={p.crm_record_id} className="border-t hover:bg-indigo-50/40" data-testid={`drill-row-${p.crm_record_id}`}>
                        <td className="py-2 px-2 font-mono">{p.Corporate_Project_ID || '-'}</td>
                        <td className="py-2 px-2">{p.PO_Date || '-'}</td>
                        <td className="py-2 px-2 font-medium">{p.Name || '-'}</td>
                        <td className="py-2 px-2">{p.Training_Name || '-'}</td>
                        <td className="py-2 px-2">{p.Mode_of_Training || '-'}</td>
                        <td className="py-2 px-2 text-right font-mono">{fmt2(rev)}</td>
                        <td className="py-2 px-2 text-right font-mono text-rose-600">{fmt2(cost)}</td>
                        <td className="py-2 px-2 text-right font-mono text-emerald-700">{fmt2(npWoGst)}</td>
                        <td className="py-2 px-2 text-right font-mono text-amber-700">{fmt2(fee)}</td>
                        <td className="py-2 px-2 text-right font-mono font-semibold text-indigo-700">{fmt2(ours)}</td>
                        <td className="py-2 px-2 text-xs">{p.Status || p.Payment_Status || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== Fixed Cost Dialog ===== */}
      <Dialog open={fcDialog} onOpenChange={setFcDialog}>
        <DialogContent className="max-w-md" data-testid="fc-dialog">
          <DialogHeader>
            <DialogTitle>{fcEdit ? 'Edit Fixed Cost' : 'Add Fixed Cost Head'}</DialogTitle>
            <DialogDescription>Monthly recurring cost paid from loan account. Annual = 12 × monthly.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Head</Label>
              <Input value={fcForm.head} onChange={e => setFcForm({ ...fcForm, head: e.target.value })} placeholder="e.g. Office Rent" data-testid="fc-head" />
            </div>
            <div>
              <Label>Monthly Amount (INR)</Label>
              <Input type="number" step="0.01" value={fcForm.monthly_amount} onChange={e => setFcForm({ ...fcForm, monthly_amount: e.target.value })} data-testid="fc-amount" />
              {fcForm.monthly_amount && <p className="text-xs text-slate-500 mt-1">Annual: <strong>{fmt((parseFloat(fcForm.monthly_amount) || 0) * 12)}</strong></p>}
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea rows={2} value={fcForm.notes} onChange={e => setFcForm({ ...fcForm, notes: e.target.value })} data-testid="fc-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFcDialog(false)}>Cancel</Button>
            <Button onClick={saveFixedCost} className="bg-indigo-950 hover:bg-indigo-900" data-testid="fc-save">{fcEdit ? 'Update' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Ad-hoc Dialog ===== */}
      <Dialog open={adhocDialog} onOpenChange={setAdhocDialog}>
        <DialogContent className="max-w-md" data-testid="adhoc-dialog">
          <DialogHeader>
            <DialogTitle>Add Ad-hoc Funder Cost</DialogTitle>
            <DialogDescription>One-off payment made for funder. FY: {selectedFY}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Date</Label>
              <Input type="date" value={adhocForm.date} onChange={e => setAdhocForm({ ...adhocForm, date: e.target.value })} data-testid="adhoc-date" />
            </div>
            <div>
              <Label>Head</Label>
              <Input value={adhocForm.head} onChange={e => setAdhocForm({ ...adhocForm, head: e.target.value })} placeholder="e.g. Funder annual audit" data-testid="adhoc-head" />
            </div>
            <div>
              <Label>Amount (INR)</Label>
              <Input type="number" step="0.01" value={adhocForm.amount} onChange={e => setAdhocForm({ ...adhocForm, amount: e.target.value })} data-testid="adhoc-amount" />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea rows={2} value={adhocForm.description} onChange={e => setAdhocForm({ ...adhocForm, description: e.target.value })} data-testid="adhoc-desc" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdhocDialog(false)}>Cancel</Button>
            <Button onClick={saveAdhoc} className="bg-indigo-950 hover:bg-indigo-900" data-testid="adhoc-save">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ===== Generic Card Drill Dialog ===== */}
      <Dialog open={drillCardOpen} onOpenChange={setDrillCardOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="card-drill-dialog">
          <DialogHeader>
            <DialogTitle data-testid="card-drill-title">{drillCardTitle}</DialogTitle>
            <DialogDescription>FY {selectedFY}</DialogDescription>
          </DialogHeader>
          {drillCardLoading && <p className="text-slate-500 py-6 text-center">Loading...</p>}
          {!drillCardLoading && drillCardData && (
            <>
              {drillCardKind === 'loans' && (
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50"><tr>
                      <th className="text-left py-2 px-3">Funder</th>
                      <th className="text-right py-2 px-3">Online</th>
                      <th className="text-right py-2 px-3">Deposit</th>
                      <th className="text-right py-2 px-3">Total</th>
                      <th className="text-left py-2 px-3">Date</th>
                    </tr></thead>
                    <tbody>
                      {drillCardData.loans.map(l => (
                        <tr key={l.id || l.funder_name} className="border-t">
                          <td className="py-2 px-3 font-medium">{l.funder_name}</td>
                          <td className="py-2 px-3 text-right font-mono">{fmt(l.online_amount)}</td>
                          <td className="py-2 px-3 text-right font-mono">{fmt(l.deposit_amount)}</td>
                          <td className="py-2 px-3 text-right font-mono font-semibold">{fmt((+l.online_amount || 0) + (+l.deposit_amount || 0))}</td>
                          <td className="py-2 px-3 text-slate-500">{l.date || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t-2 bg-slate-50 font-semibold">
                      <tr>
                        <td className="py-2 px-3">GRAND TOTAL</td>
                        <td className="py-2 px-3 text-right font-mono">{fmt(drillCardData.online_total)}</td>
                        <td className="py-2 px-3 text-right font-mono">{fmt(drillCardData.deposit_total)}</td>
                        <td className="py-2 px-3 text-right font-mono">{fmt(drillCardData.grand_total)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
              {(drillCardKind === 'invoices_billed' || drillCardKind === 'invoices_paid' || drillCardKind === 'invoices_pending') && (
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50"><tr>
                      <th className="text-left py-2 px-3">Invoice #</th>
                      <th className="text-left py-2 px-3">Date</th>
                      <th className="text-left py-2 px-3">Customer</th>
                      <th className="text-left py-2 px-3">Project ID</th>
                      <th className="text-right py-2 px-3">Total (INR)</th>
                      <th className="text-right py-2 px-3">Net Received</th>
                      <th className="text-right py-2 px-3">Balance</th>
                      <th className="text-left py-2 px-3">Status</th>
                      <th className="text-center py-2 px-3">Funder calc</th>
                    </tr></thead>
                    <tbody>
                      {drillCardData.invoices.map(inv => (
                        <tr key={inv.invoice_id} className={`border-t ${inv.excluded_from_funder ? 'opacity-50 bg-rose-50/30' : ''}`}>
                          <td className="py-2 px-3 font-mono">{inv.invoice_number}</td>
                          <td className="py-2 px-3">{inv.date}</td>
                          <td className="py-2 px-3">{inv.customer_name}</td>
                          <td className="py-2 px-3 font-mono text-slate-500">{inv.crm_project_id || '-'}</td>
                          <td className="py-2 px-3 text-right font-mono">{fmt(inv.total_inr)}</td>
                          <td className="py-2 px-3 text-right font-mono text-emerald-700">{fmt(inv.net_amount)}</td>
                          <td className="py-2 px-3 text-right font-mono text-amber-700">{fmt(inv.balance)}</td>
                          <td className="py-2 px-3"><Badge className="capitalize">{inv.status}</Badge></td>
                          <td className="py-2 px-3 text-center">
                            <Button
                              size="sm"
                              variant={inv.excluded_from_funder ? 'outline' : 'ghost'}
                              className={inv.excluded_from_funder ? 'text-emerald-600 border-emerald-200' : 'text-rose-600'}
                              onClick={async () => {
                                try {
                                  await axios.put(`${API}/funder-payout/invoices/${inv.invoice_id}/exclude`,
                                    { excluded: !inv.excluded_from_funder },
                                    { headers: headers() });
                                  toast.success(inv.excluded_from_funder ? 'Re-included in funder calc' : 'Excluded from funder calc');
                                  openCardDrill(drillCardKind, drillCardTitle);
                                  fetchSummary();
                                } catch (e) { toast.error('Failed'); }
                              }}
                              data-testid={`funder-exclude-${inv.invoice_id}`}
                            >
                              {inv.excluded_from_funder ? 'Include' : 'Exclude'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-xs text-slate-500 p-2">{drillCardData.count} invoices · greyed rows are excluded from funder payout calc but stay on the dashboard</p>
                </div>
              )}
              {drillCardKind === 'tds' && (
                <div className="space-y-3">
                  <div className="p-3 bg-purple-50 rounded-lg flex items-center justify-between">
                    <div><p className="text-xs text-purple-600">Total TDS Withheld (linked to FY projects)</p><p className="text-2xl font-bold text-purple-700">{fmt(drillCardData.total_tds)}</p></div>
                    <p className="text-xs text-slate-500">{drillCardData.count} payments · {drillCardData.project_ids_in_fy?.length || 0} CRM projects</p>
                  </div>
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50"><tr>
                        <th className="text-left py-2 px-3">Project ID</th>
                        <th className="text-left py-2 px-3">Invoice #</th>
                        <th className="text-left py-2 px-3">Customer</th>
                        <th className="text-left py-2 px-3">Payment #</th>
                        <th className="text-left py-2 px-3">Date</th>
                        <th className="text-left py-2 px-3">Mode</th>
                        <th className="text-right py-2 px-3">Paid</th>
                        <th className="text-right py-2 px-3">TDS</th>
                      </tr></thead>
                      <tbody>
                        {drillCardData.rows.map((r, i) => (
                          <tr key={i} className="border-t">
                            <td className="py-2 px-3 font-mono text-indigo-600">{r.crm_project_id || '-'}</td>
                            <td className="py-2 px-3 font-mono">{r.invoice_number}</td>
                            <td className="py-2 px-3">{r.customer_name}</td>
                            <td className="py-2 px-3 font-mono">{r.payment_number}</td>
                            <td className="py-2 px-3">{r.payment_date}</td>
                            <td className="py-2 px-3 text-slate-500">{r.payment_mode}</td>
                            <td className="py-2 px-3 text-right font-mono">{fmt(r.amount_paid)}</td>
                            <td className="py-2 px-3 text-right font-mono text-purple-700 font-semibold">{fmt(r.tds_withheld)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-slate-500">Only TDS on invoices linked (via crm_project_id) to CRM Corp_Tracker projects whose PO_Date is in this FY is counted. Payment date is not filtered.</p>
                </div>
              )}
              {(drillCardKind === 'corp_costs' || drillCardKind === 'venue_costs') && drillCardData.projects && (
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50"><tr>
                      <th className="text-left py-2 px-2">Proj ID</th>
                      <th className="text-left py-2 px-2">PO Date</th>
                      <th className="text-left py-2 px-2">Company</th>
                      <th className="text-left py-2 px-2">Training</th>
                      <th className="text-right py-2 px-2">Revenue</th>
                      <th className="text-right py-2 px-2">Cost</th>
                      <th className="text-right py-2 px-2">NP (W/O GST)</th>
                    </tr></thead>
                    <tbody>
                      {drillCardData.projects.map(p => (
                        <tr key={p.crm_record_id} className="border-t">
                          <td className="py-2 px-2 font-mono">{p.Corporate_Project_ID}</td>
                          <td className="py-2 px-2">{p.PO_Date}</td>
                          <td className="py-2 px-2 font-medium">{p.Name || '-'}</td>
                          <td className="py-2 px-2">{p.Training_Name || '-'}</td>
                          <td className="py-2 px-2 text-right font-mono">{fmt(p.Amount_without_GST)}</td>
                          <td className="py-2 px-2 text-right font-mono text-rose-700">{fmt(p.Total_Costs)}</td>
                          <td className="py-2 px-2 text-right font-mono text-emerald-700">{fmt(p.Net_Profit_W_O_GST)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* ===== Quarter Calculation Breakdown Dialog ===== */}
      <Dialog open={calcOpen} onOpenChange={setCalcOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="quarter-calc-dialog">
          <DialogHeader>
            <DialogTitle>{calcQ?.key} Calculation Breakdown · FY {selectedFY}</DialogTitle>
            <DialogDescription>{calcQ?.period?.start} — {calcQ?.period?.end}</DialogDescription>
          </DialogHeader>
          {calcQ && (() => {
            const c = calcQ.corporate, v = calcQ.venue;
            const funderTotal = calcQ.funder_fee_total;
            const teamTotal = calcQ.our_share_total;
            const tds = calcQ.tds_withheld;
            const netOwe = calcQ.net_we_owe_funder_after_tds;
            return (
              <div className="space-y-5 text-sm" data-testid="quarter-calc-content">
                {/* Corporate */}
                <div className="border-2 border-indigo-200 rounded-lg p-4 space-y-2">
                  <h4 className="font-semibold text-indigo-950 flex items-center gap-2">
                    <Landmark className="w-4 h-4" /> Corporate Training · {c.project_count} projects
                  </h4>
                  <CalcLine label="Revenue (excl GST)" value={c.revenue_excl_gst} />
                  <CalcLine label="Total project cost" value={c.cost} neg />
                  <CalcLine label="Net (revenue − cost)" value={c.revenue_excl_gst - c.cost} bold />
                  <div className="pl-4 border-l-2 border-amber-300 space-y-1 my-2">
                    <p className="text-xs text-slate-500">Funder fee rule: <strong>10% × Revenue (excl GST)</strong></p>
                    <p className="text-xs font-mono">10% × {fmt(c.revenue_excl_gst)} = <span className="text-amber-700 font-bold">{fmt(c.funder_fee)}</span></p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-md border-2 border-amber-200 bg-amber-50 p-2">
                      <p className="text-[10px] text-amber-700 font-semibold uppercase">Funder Share</p>
                      <p className="text-base font-bold text-amber-700 font-mono">{fmt(c.funder_fee)}</p>
                    </div>
                    <div className="rounded-md border-2 border-indigo-300 bg-indigo-50 p-2">
                      <p className="text-[10px] text-indigo-700 font-semibold uppercase">Corporate Team Share</p>
                      <p className="text-base font-bold text-indigo-700 font-mono">{fmt(c.our_share)}</p>
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-500">Corp Team = ({fmt(c.revenue_excl_gst)} − {fmt(c.cost)}) − {fmt(c.funder_fee)} = <strong>{fmt(c.our_share)}</strong></p>
                </div>

                {/* Venue */}
                <div className="border-2 border-purple-200 rounded-lg p-4 space-y-2">
                  <h4 className="font-semibold text-purple-950 flex items-center gap-2">
                    <Landmark className="w-4 h-4" /> Venue · {v.project_count} projects
                  </h4>
                  <CalcLine label="Revenue (excl GST)" value={v.revenue_excl_gst} />
                  <CalcLine label="Total project cost" value={v.cost} neg />
                  <CalcLine label="Net Profit (W/O GST)" value={v.net_profit_wo_gst} bold highlight="emerald" />
                  <div className="pl-4 border-l-2 border-amber-300 space-y-1 my-2">
                    <p className="text-xs text-slate-500">Funder fee rule: <strong>70% × Net Profit (W/O GST)</strong></p>
                    <p className="text-xs font-mono">70% × {fmt(v.net_profit_wo_gst)} = <span className="text-amber-700 font-bold">{fmt(v.funder_fee)}</span></p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-md border-2 border-amber-200 bg-amber-50 p-2">
                      <p className="text-[10px] text-amber-700 font-semibold uppercase">Funder Share (70%)</p>
                      <p className="text-base font-bold text-amber-700 font-mono">{fmt(v.funder_fee)}</p>
                    </div>
                    <div className="rounded-md border-2 border-purple-300 bg-purple-50 p-2">
                      <p className="text-[10px] text-purple-700 font-semibold uppercase">Corporate Team Share (30%)</p>
                      <p className="text-base font-bold text-purple-700 font-mono">{fmt(v.our_share)}</p>
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-500">Corp Team = {fmt(v.net_profit_wo_gst)} − {fmt(v.funder_fee)} = <strong>{fmt(v.our_share)}</strong></p>
                </div>

                {/* Quarter Totals */}
                <div className="border-2 border-slate-300 rounded-lg p-4 space-y-3 bg-slate-50/40">
                  <h4 className="font-semibold text-slate-900">Quarter Totals</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3">
                      <p className="text-xs text-amber-700 font-semibold uppercase tracking-wide">Total Funder Share</p>
                      <p className="text-2xl font-bold text-amber-700 font-mono">{fmt(funderTotal)}</p>
                      <p className="text-[11px] text-slate-500 mt-1">Corp {fmt(c.funder_fee)} + Venue {fmt(v.funder_fee)}</p>
                    </div>
                    <div className="rounded-lg border-2 border-indigo-400 bg-indigo-50 p-3">
                      <p className="text-xs text-indigo-700 font-semibold uppercase tracking-wide">Total Corporate Team Share</p>
                      <p className="text-2xl font-bold text-indigo-700 font-mono">{fmt(teamTotal)}</p>
                      <p className="text-[11px] text-slate-500 mt-1">Corp {fmt(c.our_share)} + Venue {fmt(v.our_share)}</p>
                    </div>
                  </div>
                  {tds > 0 && (
                    <>
                      <div className="border-t pt-3 space-y-2">
                        <CalcLine label="Total Funder Share (gross)" value={funderTotal} highlight="amber" bold />
                        <CalcLine label="− TDS already paid to funder (this FY's projects)" value={tds} neg highlight="purple" />
                        <CalcLine label="Net we owe funder (after TDS)" value={netOwe} bold highlight="rose" />
                      </div>
                      <p className="text-[11px] text-slate-500">
                        TDS shown above is the project-FY strict subset (only invoices whose CRM project belongs to FY {selectedFY}). The quarter card includes only TDS whose linked CRM project's PO_Date is in this quarter.
                      </p>
                    </>
                  )}
                  {/* Final settlement after reimbursement */}
                  {(() => {
                    const fixedQ = calcQ.fixed_costs || 0;
                    const adhocQ = calcQ.adhoc_costs || 0;
                    const reimb = fixedQ + adhocQ;
                    const baseOwed = tds > 0 ? netOwe : funderTotal;
                    const finalOwe = baseOwed - reimb;
                    if (reimb === 0 && tds === 0) return null;
                    return (
                      <div className="border-t-2 border-dashed border-slate-300 pt-3 mt-3 space-y-2" data-testid="quarter-final-settlement">
                        <h5 className="font-semibold text-slate-800 text-sm">Final Settlement (after reimbursable costs)</h5>
                        <CalcLine label="We owe funder (after TDS)" value={baseOwed} bold highlight="rose" />
                        <CalcLine label="− Fixed costs (this quarter)" value={fixedQ} neg highlight="emerald" />
                        <CalcLine label="− Ad-hoc costs (this quarter)" value={adhocQ} neg highlight="emerald" />
                        <CalcLine label="= Fixed + Ad-hoc total (funder reimburses us)" value={reimb} bold highlight="emerald" />
                        <div className={`rounded-md border-2 p-3 mt-2 ${finalOwe >= 0 ? 'border-rose-300 bg-rose-50' : 'border-emerald-300 bg-emerald-50'}`}>
                          <div className="flex justify-between items-baseline">
                            <span className={`text-xs font-semibold uppercase tracking-wide ${finalOwe >= 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                              {finalOwe >= 0 ? 'Final · We owe Funder' : 'Final · Funder owes Us'}
                            </span>
                            <span className={`text-xl font-bold font-mono ${finalOwe >= 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{fmt(Math.abs(finalOwe))}</span>
                          </div>
                          <p className="text-[11px] text-slate-500 mt-1 font-mono">
                            {fmt(baseOwed)} − {fmt(reimb)} = {finalOwe >= 0 ? '' : '−'}{fmt(Math.abs(finalOwe))}
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Row = ({ label, value, highlight }) => {
  const colorMap = { amber: 'text-amber-700', emerald: 'text-emerald-700', rose: 'text-rose-700' };
  return (
    <div>
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`font-mono font-semibold ${highlight ? colorMap[highlight] : 'text-slate-800'}`}>{value}</p>
    </div>
  );
};

const CalcLine = ({ label, value, neg, bold, highlight }) => {
  const colorMap = { amber: 'text-amber-700', emerald: 'text-emerald-700', rose: 'text-rose-700', purple: 'text-purple-700' };
  const sign = neg ? '−' : '+';
  return (
    <div className="flex justify-between items-baseline text-sm">
      <span className={`${bold ? 'font-semibold' : ''} text-slate-700`}>{label}</span>
      <span className={`font-mono ${bold ? 'font-bold' : ''} ${highlight ? colorMap[highlight] : 'text-slate-800'}`}>
        {neg ? <span className="text-rose-600 mr-1">{sign}</span> : null}
        {fmt(value)}
      </span>
    </div>
  );
};

const SettleBox = ({ label, value, tone = 'slate', emphasis, sub, testid }) => {
  const tones = {
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    purple: 'border-purple-200 bg-purple-50 text-purple-700',
    rose: 'border-rose-200 bg-rose-50 text-rose-700',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    slate: 'border-slate-200 bg-white text-slate-700'
  };
  return (
    <div className={`rounded-md border-2 ${tones[tone]} px-3 py-2 ${emphasis ? 'shadow-sm' : ''}`} data-testid={testid}>
      <p className="text-[10px] font-semibold uppercase tracking-wide opacity-80">{label}</p>
      <p className={`font-mono ${emphasis ? 'text-lg font-bold' : 'text-base font-semibold'}`}>{fmt(value)}</p>
      {sub && <p className="text-[10px] opacity-70 mt-0.5">{sub}</p>}
    </div>
  );
};

const SettleOp = ({ op }) => (
  <div className="hidden md:flex items-center justify-center text-xl font-bold text-slate-400">{op}</div>
);

const CashCard = ({ label, value, sub, color, Icon, onClick, testid }) => {
  const colors = {
    emerald: 'border-emerald-200 bg-gradient-to-br from-emerald-50 to-white text-emerald-700',
    rose: 'border-rose-200 bg-gradient-to-br from-rose-50 to-white text-rose-700',
    blue: 'border-blue-200 bg-gradient-to-br from-blue-50 to-white text-blue-700',
    amber: 'border-amber-200 bg-gradient-to-br from-amber-50 to-white text-amber-700',
    purple: 'border-purple-200 bg-gradient-to-br from-purple-50 to-white text-purple-700'
  };
  const bgIcon = { emerald: 'bg-emerald-100', rose: 'bg-rose-100', blue: 'bg-blue-100', amber: 'bg-amber-100', purple: 'bg-purple-100' };
  return (
    <Card
      className={`${colors[color]} ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
      data-testid={testid}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 ${bgIcon[color]} rounded-lg flex items-center justify-center`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <p className="text-xs">{label}</p>
            <p className="text-lg font-bold truncate">{fmt(value)}</p>
            {sub && <p className="text-xs text-slate-500 truncate">{sub}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FunderPayoutPage;

